import { fromEntries } from "./browserCompat";
const normalizeText = (value) => String(value ?? "").trim();

export const safeTextSx = {
  minWidth: 0,
  maxWidth: "100%",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
  hyphens: "auto",
};

export const safeTitleSx = {
  minWidth: 0,
  maxWidth: "100%",
  overflowWrap: "normal",
  wordBreak: "normal",
  hyphens: "none",
};

export const safeButtonTextSx = {
  ...safeTextSx,
  whiteSpace: "normal",
  textAlign: "center",
  lineHeight: 1.25,
};

export function longestTitleWordLength(value) {
  const words = normalizeText(value).split(/\s+/).filter(Boolean);
  return words.reduce((longest, word) => Math.max(longest, Array.from(word).length), 0);
}

export function titleWordFitScale(value) {
  const longest = longestTitleWordLength(value);
  if (longest >= 30) return 0.62;
  if (longest >= 24) return 0.7;
  if (longest >= 20) return 0.78;
  if (longest >= 16) return 0.87;
  if (longest >= 13) return 0.94;
  return 1;
}

function scaleFontSizeValue(value, factor) {
  if (value == null || factor === 1) return value;
  if (typeof value === "number") return value * factor;
  if (typeof value === "string") {
    return value.replace(/(-?\d*\.?\d+)(rem|px|vw|vh|svh)/g, (_, number, unit) => {
      const scaled = Math.round(Number(number) * factor * 1000) / 1000;
      return `${scaled}${unit}`;
    });
  }
  if (Array.isArray(value)) return value.map((item) => scaleFontSizeValue(item, factor));
  if (typeof value === "object") {
    return fromEntries(Object.entries(value).map(([key, item]) => [key, scaleFontSizeValue(item, factor)]));
  }
  return value;
}

export function textLength(value) {
  return normalizeText(value).length;
}

export function shouldCollapseText(
  value,
  { characterLimit = 320, lineLimit = 8 } = {}
) {
  const text = normalizeText(value);
  if (!text) return false;

  const explicitLineCount = text.split(/\r\n|\r|\n/).length;
  return text.length > characterLimit || explicitLineCount > lineLimit;
}

export function renderedTextExceedsLines(element, lineLimit = 8) {
  if (!element || typeof window === "undefined") return false;

  const normalizedLineLimit = Math.max(1, Number(lineLimit) || 1);
  const computedStyle = window.getComputedStyle(element);
  const lineHeight = Number.parseFloat(computedStyle.lineHeight);

  if (!Number.isFinite(lineHeight) || lineHeight <= 0) {
    return shouldCollapseText(element.textContent, {
      lineLimit: normalizedLineLimit,
    });
  }

  const maximumVisibleHeight = lineHeight * normalizedLineLimit;
  return element.scrollHeight - maximumVisibleHeight > 1;
}

export function heroTitleFontSize(value) {
  const length = textLength(value);
  let size;

  if (length > 150) {
    size = {
      xs: "clamp(1.75rem, 8.5vw, 2.75rem)",
      sm: "clamp(2.3rem, 5.5vw, 3.7rem)",
      md: "clamp(2.8rem, 4.5vw, 4.8rem)",
    };
  } else if (length > 95) {
    size = {
      xs: "clamp(2rem, 9.5vw, 3.15rem)",
      sm: "clamp(2.8rem, 6vw, 4.4rem)",
      md: "clamp(3.4rem, 5vw, 5.6rem)",
    };
  } else if (length > 55) {
    size = {
      xs: "clamp(2.3rem, 10.5vw, 3.7rem)",
      sm: "clamp(3.2rem, 6.8vw, 5.25rem)",
      md: "clamp(4rem, 5.5vw, 6.4rem)",
    };
  } else {
    size = {
      xs: "clamp(2.8rem, 13vw, 4.6rem)",
      sm: "clamp(4rem, 8vw, 6.5rem)",
      md: "clamp(4.8rem, 6.5vw, 7.4rem)",
    };
  }

  return scaleFontSizeValue(size, titleWordFitScale(value));
}

export function heroSubtitleFontSize(value) {
  return textLength(value) > 420
    ? { xs: ".94rem", sm: "1.04rem", md: "1.12rem" }
    : { xs: "1rem", sm: "1.15rem", md: "1.25rem" };
}

export function heroPhraseFontSize(value) {
  const length = textLength(value);

  if (length > 90) {
    return {
      xs: "clamp(1.45rem, 6.8vw, 2.4rem)",
      sm: "clamp(1.9rem, 4vw, 3rem)",
      md: "clamp(2.2rem, 3.4vw, 3.7rem)",
    };
  }

  if (length > 48) {
    return {
      xs: "clamp(1.7rem, 7.6vw, 2.9rem)",
      sm: "clamp(2.2rem, 4.5vw, 3.6rem)",
      md: "clamp(2.6rem, 4vw, 4.1rem)",
    };
  }

  return {
    xs: "clamp(2rem, 9vw, 3.6rem)",
    sm: "clamp(2.8rem, 5vw, 4.5rem)",
  };
}

export function sectionTitleFontSize(value, fallback = undefined) {
  const length = textLength(value);
  let size = fallback;

  if (length > 120) {
    size = { xs: "1.75rem", sm: "2.15rem", md: "2.55rem" };
  } else if (length > 70) {
    size = { xs: "2rem", sm: "2.45rem", md: "3rem" };
  }

  return scaleFontSizeValue(size, titleWordFitScale(value));
}
